import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, PlayCircle } from "lucide-react";
import { api, type ApiVideo } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";

const PROGRESS_KEY_PREFIX = "mvg_video_progress:";
const RESUME_THRESHOLD_SEC = 10; // 不到 10 秒就别恢复了
const COMPLETE_RATIO = 0.97; // 看到 97% 视为看完，清掉进度

function progressKey(id: number) {
  return `${PROGRESS_KEY_PREFIX}${id}`;
}

function loadProgress(id: number): { t: number; d: number } | null {
  try {
    const raw = localStorage.getItem(progressKey(id));
    if (!raw) return null;
    const j = JSON.parse(raw);
    if (typeof j.t === "number" && typeof j.d === "number") return j;
    return null;
  } catch {
    return null;
  }
}

function saveProgress(id: number, t: number, d: number) {
  try {
    localStorage.setItem(progressKey(id), JSON.stringify({ t, d, ts: Date.now() }));
  } catch {}
}

function clearProgress(id: number) {
  try {
    localStorage.removeItem(progressKey(id));
  } catch {}
}

export default function WatchPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [video, setVideo] = useState<ApiVideo | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const lastSaveRef = useRef(0);
  const [resumeNotice, setResumeNotice] = useState<string | null>(null);
  const accRef = useRef(0); // 累计待上报的有效观看秒数
  const lastTimeRef = useRef(0);
  const [checkinToast, setCheckinToast] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api
      .getVideo(Number(id))
      .then((r) => setVideo(r.video))
      .catch((e) => setErr((e as Error).message))
      .finally(() => setLoading(false));
  }, [id]);

  // 每 10s 上报有效观看时长（服务端为准，用于打卡）
  useEffect(() => {
    if (!video) return;
    const flush = () => {
      const pos = videoRef.current?.currentTime || 0;
      const dur = videoRef.current?.duration || 0;
      const delta = Math.round(accRef.current);
      if (delta <= 0) return null;
      accRef.current = 0;
      return api
        .reportProgress({ videoId: video.id, position: pos, duration: dur, deltaSec: delta })
        .then((r) => {
          if (r.justCheckedIn) {
            setCheckinToast("恭喜看完整节课，打卡成功 🎉");
            setTimeout(() => setCheckinToast(null), 5000);
          }
        })
        .catch(() => {});
    };
    const timer = setInterval(() => flush(), 10000);
    return () => {
      clearInterval(timer);
      flush();
    };
  }, [video]);

  if (loading) {
    return <main className="container-narrow py-20 text-center text-sm text-ink-400">加载中…</main>;
  }
  if (err || !video) {
    return (
      <main className="container-narrow py-20 text-center">
        <p className="text-ink-400">视频不存在或已下架</p>
        <Link to="/" className="btn-ghost mt-6 inline-flex">
          <ArrowLeft className="h-4 w-4" /> 返回视频库
        </Link>
      </main>
    );
  }

  return (
    <main className="bg-ink-800 pb-16 text-white">
      <div className="border-b border-white/5 bg-ink-900/60">
        <div className="container-narrow flex items-center gap-3 py-3 text-xs text-white/60">
          <Link to="/" className="hover:text-white">视频库</Link>
          <span className="text-white/30">/</span>
          <span className="truncate text-white/80">{video.title}</span>
        </div>
      </div>

      <div className="container-narrow py-8">
        {resumeNotice && (
          <div className="mb-3 rounded-md border border-white/10 bg-white/5 px-3 py-2 text-xs text-white/70">
            ⏯ {resumeNotice}
          </div>
        )}
        {checkinToast && (
          <div className="mb-3 rounded-md border border-emerald-400/30 bg-emerald-500/15 px-3 py-2 text-sm text-emerald-200">
            ✅ {checkinToast}
          </div>
        )}
        <div className="overflow-hidden rounded-xl bg-black ring-1 ring-white/10">
          <div className="relative aspect-video w-full">
            {video.vod_url ? (
              /^https?:.+\.(mp4|m3u8|webm|mov|ogv|ogg)(\?.*)?$/i.test(video.vod_url) ? (
                <video
                  ref={videoRef}
                  src={video.vod_url}
                  poster={video.cover_path}
                  controls
                  controlsList="nodownload"
                  playsInline
                  preload="metadata"
                  className="h-full w-full"
                  onLoadedMetadata={(e) => {
                    const el = e.currentTarget;
                    const saved = loadProgress(Number(id));
                    if (
                      saved &&
                      saved.t > RESUME_THRESHOLD_SEC &&
                      el.duration > 0 &&
                      saved.t < el.duration - 5
                    ) {
                      el.currentTime = saved.t;
                      const mm = Math.floor(saved.t / 60);
                      const ss = String(Math.floor(saved.t % 60)).padStart(2, "0");
                      setResumeNotice(`已从上次 ${mm}:${ss} 继续`);
                      setTimeout(() => setResumeNotice(null), 3500);
                    }
                  }}
                  onPause={(e) => { lastTimeRef.current = e.currentTarget.currentTime; }}
                  onSeeking={(e) => { lastTimeRef.current = e.currentTarget.currentTime; }}
                  onTimeUpdate={(e) => {
                    const el = e.currentTarget;
                    // 累计有效观看：仅播放中且页面可见（防止后台挂机刷时长）
                    if (!el.paused && document.visibilityState === "visible") {
                      const d = el.currentTime - lastTimeRef.current;
                      if (d > 0 && d < 5) accRef.current += d;
                    }
                    lastTimeRef.current = el.currentTime;
                    const now = Date.now();
                    if (now - lastSaveRef.current < 5000) return; // 5s throttle
                    lastSaveRef.current = now;
                    if (el.duration && el.currentTime > RESUME_THRESHOLD_SEC) {
                      if (el.currentTime / el.duration >= COMPLETE_RATIO) {
                        clearProgress(Number(id));
                      } else {
                        saveProgress(Number(id), el.currentTime, el.duration);
                      }
                    }
                  }}
                  onEnded={() => clearProgress(Number(id))}
                />
              ) : (
                // 非视频文件直链（例如腾讯 VOD 提供的 iframe 嵌入 / 播放页 URL）
                <iframe
                  src={video.vod_url}
                  className="h-full w-full"
                  allowFullScreen
                  allow="autoplay; encrypted-media"
                />
              )
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-ink-900 via-ink-800 to-brand/30 text-center">
                <PlayCircle className="h-16 w-16 text-white/30" />
                <div className="mt-3 text-sm text-white/50">VOD 链接为空</div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6">
          <div className="flex items-center gap-2 text-[11px]">
            <span className="rounded-full bg-brand/20 px-3 py-1 font-semibold uppercase tracking-wider text-brand-light">
              {video.category_name}
            </span>
          </div>
          <h1 className="mt-3 font-display text-2xl font-semibold leading-tight md:text-3xl">
            {video.title}
          </h1>
          {video.description && (
            <p className="mt-3 max-w-prose text-sm leading-relaxed text-white/70">
              {video.description}
            </p>
          )}
        </div>

        <div className="mt-10 border-t border-white/10 pt-4 text-[11px] text-white/40">
          {user?.username} · 内部访问 · 禁止录屏外传
        </div>
      </div>
    </main>
  );
}
